<template>
  <v-dialog
    eager
    @update:model-value="change"
    :model-value="shouldShow"
    :max-width="width"
    :persistent="persistent"
    scrollable
    @keydown.esc="choose(false)"
    @keydown.enter.prevent="choose(true)">
    <v-card tile>
      <template v-if="deleteConfirmation">
        <div class="d-flex flex-column align-center justify-center my-6">
          <v-icon size="48" color="red">{{ icon }}</v-icon>
          <h2 class="mt-3">{{ title }}</h2>
          <h3 class="text--secondary" v-if="entityName">
            {{ entityName }}
          </h3>
        </div>
        <v-divider></v-divider>
      </template>
      <template v-else>
        <v-toolbar class="px-4" v-if="Boolean(title)" :dark="darkTheme" :color="color" dense flat>
          <v-icon size="large" v-if="Boolean(icon)" start>{{ icon }}</v-icon>
          <v-toolbar-title :class="{ 'white--text': darkTheme }" v-text="title" />
          <template v-if="allowClose">
            <v-spacer></v-spacer>
            <v-btn size="small" icon class="mr-3" @click="$emit('close')">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-toolbar>
      </template>
      <v-card-text class="text-body-1 text-body-1 mt-5">
        <slot v-if="translatedMessage" name="message"></slot>
        <span v-if="!$slots.message && translatedMessage" v-html="translatedMessage"></span>

        <v-alert
          v-if="deleteConfirmation && showDeleteWarning"
          variant="tonal"
          prominent
          border="start"
          color="error"
          class="my-5">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          <span>
            <strong>{{ $t('global.basicTerms.warning') }}!</strong>
            <strong>{{ $t('global.phrases.thisActionCannotBeUndone') }}.</strong>
          </span>
        </v-alert>
        <div v-if="confirmationInputText" class="mt-4">
          <p
            class="mb-2"
            v-html="
              $t('lists.common.messages.typeToConfirm', { message: confirmationInputText })
            "></p>
          <text-field
            type="text"
            variant="outlined"
            @paste="handleConfirmPaste"
            v-model="confirmationTextValue"
            data-testid="confirm-text-field"
            :rules="[rules.confirmTextMatch]"></text-field>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <slot name="custom-actions" :choose="choose">
          <v-spacer />
          <slot name="false-action" :choose="choose">
            <v-btn
              v-if="Boolean(translatedButtonFalseText)"
              :color="buttonFalseColor"
              :variant="buttonFalseFlat ? 'text' : undefined"
              :disabled="loading"
              @click="choose(false)">
              {{ translatedButtonFalseText }}
            </v-btn>
          </slot>
          <slot name="true-action" :choose="choose">
            <v-btn
              v-if="Boolean(translatedButtonTrueText)"
              :color="buttonTrueColor"
              :variant="buttonTrueFlat ? 'text' : undefined"
              :loading="loading"
              :disabled="!isConfirmTextMatched"
              @click="choose(true)">
              {{ translatedButtonTrueText }}
            </v-btn>
          </slot>
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { $t } from '@satellite/plugins/translator';

export default {
  props: {
    buttonTrueText: {
      type: String
    },
    buttonFalseText: {
      type: String
    },
    buttonTrueColor: {
      type: String,
      default: 'primary'
    },
    buttonFalseColor: {
      type: String,
      default: 'grey'
    },
    buttonFalseFlat: {
      type: Boolean,
      default: true
    },
    buttonTrueFlat: {
      type: Boolean,
      default: true
    },
    color: {
      type: String,
      default: 'warning'
    },
    icon: {
      type: String,
      default: 'mdi-help-circle-outline'
    },
    message: {
      type: String,
      required: false
    },
    persistent: Boolean,
    title: {
      type: String,
      default: 'Confirm'
    },
    width: {
      type: Number,
      default: 600
    },
    confirmationInputText: {
      type: String,
      default: null
    },
    preventConfirmPaste: {
      type: Boolean,
      required: false,
      default: true
    },
    pasteRejectionMsg: {
      type: String,
      required: false
    },
    shouldShow: {
      type: Boolean,
      required: false,
      default: true
    },
    isManualMode: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    deleteConfirmation: {
      type: Boolean,
      required: false,
      default: false
    },
    showDeleteWarning: {
      type: Boolean,
      required: false,
      default: true
    },
    entityName: {
      type: String,
      required: false,
      default: null
    },
    darkTheme: {
      type: Boolean,
      required: false,
      default: true
    },
    allowClose: {
      type: Boolean,
      required: false,
      default: false
    },
    // This is weird, but this.notify is not available when confirm is called in script of component
    // So we pass it instead
    pasteNotifyFn: {
      type: Function,
      required: false
    }
  },
  setup() {},
  data() {
    return {
      value: false,
      confirmationTextValue: '',
      rules: {
        confirmTextMatch: () =>
          this.isConfirmTextMatched || `Please confirm by typing "${this.confirmationInputText}"`
      }
    };
  },
  computed: {
    translatedButtonTrueText() {
      const deleteConfirmation = this.buttonTrueText ?? $t('global.buttons.yesDelete');
      const otherConfirmation = this.buttonTrueText ?? $t('global.buttons.confirm');
      return this.deleteConfirmation ? deleteConfirmation : otherConfirmation;
    },
    translatedButtonFalseText() {
      return this.buttonFalseText ?? $t('global.buttons.nevermind');
    },
    isConfirmTextMatched() {
      return (
        !this.confirmationInputText ||
        this.confirmationInputText.toLowerCase() === this.confirmationTextValue.toLowerCase()
      );
    },
    translatedMessage() {
      return this.message ?? $t('global.phrases.areYouSure');
    },
    translatedPasteRejectionMessage() {
      return (
        this.pasteRejectionMsg ?? $t('global.messages.theConfirmationFieldDoesNotSupportPasting')
      );
    }
  },
  methods: {
    $t,
    choose(value) {
      if (value && !this.isConfirmTextMatched) {
        return;
      }
      this.$emit('result', value);
      this.value = value;

      this.destroyComponent();
    },
    change() {
      this.destroyComponent();
    },
    destroyComponent() {
      if (!this.isManualMode) {
        if (this.$el?.parentNode) {
          this.$el.parentNode.removeChild(this.$el);
        }
      }
    },
    handleConfirmPaste(e) {
      if (this.preventConfirmPaste) {
        e.preventDefault();
        if (this.pasteNotifyFn) {
          this.pasteNotifyFn(this.translatedPasteRejectionMessage, 'error');
        } else {
          this.notify(this.translatedPasteRejectionMessage, 'error');
        }
      }
    }
  }
};
</script>
